import { css } from 'lit-element';
import { dispatchCustomEvent } from './custom-event-dispatcher.js';
import { convertToRem } from './pixel-to-rem-convertor.js';
import { convertToPixel } from './rem-to-pixel-convertor.js';

/**
 * #### PopupPlacementMixin
 *
 * This is basically a mixin to implement in any popup like
 * control like tooltip, popover.
 *
 * This mixin takes care of positioning the popup within
 * the viewport when a specified placement is given.
 *
 * If the popup placement corresponds to a placement outside
 * the viewport, it will either flip or find the best placement
 * or the best co-ordinates so that there is no overlap between
 * the popup and the target and the popup is always within viewport.
 *
 * For implementation details, please refer the popup placement
 * docs.
 *
 * This mixin can be extended in future to use for overlays like
 * datepicker calendar overlay, input-number keypad overlay, etc.
 *
 * Right now, the scroll-behavior is similar to scroll-action=cancel.
 * Popup hides on scrolling via wheel or on click outside the popup and target
 */

export const PopupPlacementMixin = (base) =>
  class PlacementMixin extends base {
    static get styles() {
      return [
        css`
          :host {
            position: fixed;
          }
        `,
      ];
    }
    static get properties() {
      return {
        placement: { type: String, reflect: true },
        visible: { type: Boolean, reflect: true },
        /** The viewport - default viewport is body */
        viewport: { type: Object },
        /** The viewport padding in rem - default value is 0.5rem */
        viewportPadding: { type: Number },
        /** The distance between target and popup in rem - default value is 0.5rem */
        targetPopupDistance: { type: Number },
      };
    }
    constructor() {
      super();
      this.viewport = document.body;
      this.viewportPadding = 0.5;
      this.targetPopupDistance = 0.5;
    }
    connectedCallback() {
      super.connectedCallback();
      this._findOffsetParentListener = this._findOffsetParent.bind(this);
      this._documentMouseDownListener = this._documentMouseDownActions.bind(this);
      this._documentMouseWheelListener = this._documentMouseWheelActions.bind(this);
      this.addEventListener('find-offset-parent', this._findOffsetParentListener);
    }
    updated(changedProperties) {
      super.updated(changedProperties);
      if (changedProperties.has('visible') && !this.visible) {
        document.body.removeEventListener('wheel', this._documentMouseWheelListener);
        document.body.removeEventListener('mousedown', this._documentMouseDownListener);
      }
    }
    getTarget(target) {
      if (target && typeof target === 'string') {
        const targetElement = this.parentElement
          ? this.parentElement.querySelector(`[id="${target}"]`) || document.getElementById(target)
          : document.getElementById(target);
        return targetElement;
      } else if (target instanceof HTMLElement) {
        return target;
      } else {
        throw new Error(
          `\n\nThe "target" property is not of valid type.` +
            `\nThe "target" property can take string value corresponding to the id of the target.` +
            `\nIt can also have its value equal to a DOM Node reference (instance of HTMLElement)\n`
        );
      }
    }
    show() {
      if (this.placement && this._targetEl) {
        this._initialiseRectData();
        this._setCoOrdinates();
      }
      this.classList.add('is-visible');
    }
    hide() {
      this.classList.remove('is-visible');
    }
    disconnectedCallback() {
      this.removeEventListener('find-offset-parent', this._findOffsetParentListener);
      document.body.removeEventListener('wheel', this._documentMouseWheelListener);
      document.body.removeEventListener('mousedown', this._documentMouseDownListener);
      super.disconnectedCallback();
    }
    _getFlippedPositionForPlacement(placement) {
      const flipPositionDictionary = {
        top: 'bottom',
        'top-right': 'bottom-right',
        'top-left': 'bottom-left',
        bottom: 'top',
        'bottom-left': 'top-left',
        'bottom-right': 'top-right',
        left: 'right',
        'left-up': 'right-up',
        'left-down': 'right-down',
        right: 'left',
        'right-up': 'left-up',
        'right-down': 'left-down',
      };
      return flipPositionDictionary[placement];
    }
    _getAlternatePlacements(placement) {
      const alternatePlacementsDictionary = {
        top: ['top-left', 'top-right'],
        left: ['left-up', 'left-down'],
        bottom: ['bottom-left', 'bottom-right'],
        right: ['right-up', 'right-down'],
        'top-right': ['top', 'top-left'],
        'top-left': ['top', 'top-right'],
        'left-up': ['left', 'left-down'],
        'left-down': ['left', 'left-up'],
        'bottom-left': ['bottom', 'bottom-right'],
        'bottom-right': ['bottom', 'bottom-left'],
        'right-up': ['right', 'right-down'],
        'right-down': ['right', 'right-up'],
      };
      return alternatePlacementsDictionary[placement];
    }
    _satisfiesFlipCondition(placement) {
      const positionRectForPlacement = this._getPopupRectDataForPlacement(placement);
      const flipConditionDictionary = {
        top: positionRectForPlacement.top < this._viewportRectData.top,
        'top-left': positionRectForPlacement.top < this._viewportRectData.top,
        'top-right': positionRectForPlacement.top < this._viewportRectData.top,
        bottom: positionRectForPlacement.bottom > this._viewportRectData.bottom,
        'bottom-left': positionRectForPlacement.bottom > this._viewportRectData.bottom,
        'bottom-right': positionRectForPlacement.bottom > this._viewportRectData.bottom,
        left: positionRectForPlacement.left < this._viewportRectData.left,
        'left-up': positionRectForPlacement.left < this._viewportRectData.left,
        'left-down': positionRectForPlacement.left < this._viewportRectData.left,
        right: positionRectForPlacement.right > this._viewportRectData.right,
        'right-up': positionRectForPlacement.right > this._viewportRectData.right,
        'right-down': positionRectForPlacement.right > this._viewportRectData.right,
      };
      return flipConditionDictionary[placement];
    }
    _isWithinViewport(placement) {
      const positionRectForPlacement = this._getPopupRectDataForPlacement(placement);
      return (
        positionRectForPlacement.left >= this._viewportRectData.left &&
        positionRectForPlacement.right <= this._viewportRectData.right &&
        positionRectForPlacement.top >= this._viewportRectData.top &&
        positionRectForPlacement.bottom <= this._viewportRectData.bottom
      );
    }
    _getPopupRectDataForPlacement(placement) {
      let left, right, top, bottom;
      const targetPopupDistanceInPixels = convertToPixel(this.targetPopupDistance);
      switch (placement) {
        case 'top':
          top = this._targetRectData.top - this._thisRectData.height - targetPopupDistanceInPixels;
          left =
            this._targetRectData.left +
            this._targetRectData.width / 2 -
            this._thisRectData.width / 2;
          break;
        case 'top-right':
          top = this._targetRectData.top - this._thisRectData.height - targetPopupDistanceInPixels;
          left = this._targetRectData.right - this._thisRectData.width;
          break;
        case 'top-left':
          top = this._targetRectData.top - this._thisRectData.height - targetPopupDistanceInPixels;
          left = this._targetRectData.left;
          break;
        case 'bottom':
          top = this._targetRectData.bottom + targetPopupDistanceInPixels;
          left =
            this._targetRectData.left +
            this._targetRectData.width / 2 -
            this._thisRectData.width / 2;
          break;
        case 'bottom-right':
          top = this._targetRectData.bottom + targetPopupDistanceInPixels;
          left = this._targetRectData.right - this._thisRectData.width;
          break;
        case 'bottom-left':
          top = this._targetRectData.bottom + targetPopupDistanceInPixels;
          left = this._targetRectData.left;
          break;
        case 'left':
          top =
            this._targetRectData.top +
            this._targetRectData.height / 2 -
            this._thisRectData.height / 2;
          left = this._targetRectData.left - this._thisRectData.width - targetPopupDistanceInPixels;
          break;
        case 'left-up':
          top = this._targetRectData.top;
          left = this._targetRectData.left - this._thisRectData.width - targetPopupDistanceInPixels;
          break;
        case 'left-down':
          top = this._targetRectData.bottom - this._thisRectData.height;
          left = this._targetRectData.left - this._thisRectData.width - targetPopupDistanceInPixels;
          break;
        case 'right':
          top =
            this._targetRectData.top +
            this._targetRectData.height / 2 -
            this._thisRectData.height / 2;
          left = this._targetRectData.right + targetPopupDistanceInPixels;
          break;
        case 'right-up':
          top = this._targetRectData.top;
          left = this._targetRectData.right + targetPopupDistanceInPixels;
          break;
        case 'right-down':
          top = this._targetRectData.bottom - this._thisRectData.height;
          left = this._targetRectData.right + targetPopupDistanceInPixels;
          break;
      }
      right = left + this._thisRectData.width;
      bottom = top + this._thisRectData.height;
      return { top, right, bottom, left };
    }
    _initialiseRectData() {
      dispatchCustomEvent(this, 'find-offset-parent');
      document.body.removeEventListener('mousedown', this._documentMouseDownListener);
      document.body.addEventListener('mousedown', this._documentMouseDownListener);
      document.body.removeEventListener('wheel', this._documentMouseWheelListener);
      document.body.addEventListener('wheel', this._documentMouseWheelListener);
      const viewportPaddingInPixel = convertToPixel(this.viewportPadding);
      const viewportRect = this.viewport.getBoundingClientRect();

      const viewportLeft = viewportRect.left + viewportPaddingInPixel;
      const viewportRight = viewportRect.right - viewportPaddingInPixel;
      const viewportTop = viewportRect.top + viewportPaddingInPixel;
      const viewportBottom = viewportRect.bottom - viewportPaddingInPixel;

      const viewportWidth = viewportRight - viewportLeft;
      const viewportHeight = viewportBottom - viewportTop;

      this._viewportRectData = {
        left: viewportLeft,
        right: viewportRight,
        top: viewportTop,
        bottom: viewportBottom,
        width: viewportWidth,
        height: viewportHeight,
      };
      this._thisRectData = this.getBoundingClientRect();
      this._targetRectData = this._targetEl.getBoundingClientRect();
    }
    _getViewportCorrectedPlacement() {
      /**
       * Placement fixing rules start here...
       */
      let left = 0,
        top = 0;
      if (this._isWithinViewport(this.placement)) {
        ({ left, top } = this._getCurrentPlacementCoordinates());
      } else {
        if (this._satisfiesFlipCondition(this.placement)) {
          ({ left, top } = this._getFlippedCoordinates());
        } else {
          ({ left, top } = this._getAlternateCoordinates());
        }
      }
      return {
        top: convertToRem(top - this._transformParentOffsetY),
        left: convertToRem(left - this._transformParentOffsetX),
      };
    }
    _getCurrentPlacementCoordinates() {
      let top, left;
      const placementData = this._getPopupRectDataForPlacement(this.placement);
      left = placementData.left;
      top = placementData.top;
      return { left, top };
    }

    _getAlternateCoordinates() {
      let top, left;
      const alternatePlacements = this._getAlternatePlacements(this.placement);
      const alternatePlacementWithinViewport =
        this._getAlternatePlacementWithinViewport(alternatePlacements);
      if (alternatePlacementWithinViewport !== 'none') {
        const alternatePlacementData = this._getPopupRectDataForPlacement(
          alternatePlacementWithinViewport
        );
        left = alternatePlacementData.left;
        top = alternatePlacementData.top;
      } else {
        const placementData = this._getPopupRectDataForPlacement(this.placement);
        ({ top, left } = this._getViewportCorrectedCoordinates(this.placement, placementData));
      }
      return { left, top };
    }

    _getFlippedCoordinates() {
      let left, top;
      const flippedPlacement = this._getFlippedPositionForPlacement(this.placement);
      const flippedPlacementData = this._getPopupRectDataForPlacement(flippedPlacement);
      if (this._isWithinViewport(flippedPlacement)) {
        left = flippedPlacementData.left;
        top = flippedPlacementData.top;
      } else {
        ({ left, top } = this._getAlternateFlippedCoordinates(
          flippedPlacement,
          flippedPlacementData
        ));
      }
      return { left, top };
    }

    _getAlternateFlippedCoordinates(flippedPlacement, flippedPlacementData) {
      let left, top;
      const alternateFlippedPlacements = this._getAlternatePlacements(flippedPlacement);
      const alternatePlacementWithinViewport = this._getAlternatePlacementWithinViewport(
        alternateFlippedPlacements
      );
      if (alternatePlacementWithinViewport !== 'none') {
        const alternateFlippedPlacementData = this._getPopupRectDataForPlacement(
          alternatePlacementWithinViewport
        );
        left = alternateFlippedPlacementData.left;
        top = alternateFlippedPlacementData.top;
      } else {
        ({ top, left } = this._getViewportCorrectedCoordinates(
          flippedPlacement,
          flippedPlacementData
        ));
      }
      return { left, top };
    }

    _getViewportCorrectedCoordinates(placement, placementData) {
      let top = placementData.top;
      let left = placementData.left;
      if (
        placement === 'top' ||
        placement === 'top-right' ||
        placement === 'top-left' ||
        placement === 'bottom' ||
        placement === 'bottom-left' ||
        placement === 'bottom-right'
      ) {
        if (placementData.left < this._viewportRectData.left) {
          top = placementData.top;
          left = this._viewportRectData.left;
        } else if (placementData.right > this._viewportRectData.right) {
          top = placementData.top;
          left = this._viewportRectData.right - this._thisRectData.width;
        }
      } else if (
        placement === 'left' ||
        placement === 'left-up' ||
        placement === 'left-down' ||
        placement === 'right' ||
        placement === 'right-down' ||
        placement === 'right-up'
      ) {
        if (placementData.top < this._viewportRectData.top) {
          top = this._viewportRectData.top;
          left = placementData.left;
        } else if (placementData.bottom > this._viewportRectData.bottom) {
          top = this._viewportRectData.bottom - this._thisRectData.height;
          left = placementData.left;
        }
      }
      ({ left, top } = this._getViewportCorrectedForFinalCoordinates(left, top));
      return { top, left };
    }

    _getViewportCorrectedForFinalCoordinates(left, top) {
      const right = left + this._thisRectData.width;
      const bottom = top + this._thisRectData.height;
      if (left < this._viewportRectData.left) {
        left = this._viewportRectData.left;
      }
      if (right > this._viewportRectData.right) {
        left = this._viewportRectData.right - this._thisRectData.width;
      }
      if (top < this._viewportRectData.top) {
        top = this._viewportRectData.top;
      }
      if (bottom > this._viewportRectData.bottom) {
        top = this._viewportRectData.bottom - this._thisRectData.height;
      }
      return { left, top };
    }

    _getAlternatePlacementWithinViewport(placements) {
      let alternatePlacementWithinViewport = 'none';
      for (let i = 0; i < placements.length; i++) {
        const testPlacement = placements[i];
        if (this._isWithinViewport(testPlacement)) {
          alternatePlacementWithinViewport = testPlacement;
          break;
        }
      }
      return alternatePlacementWithinViewport;
    }

    _setCoOrdinates() {
      const viewportCorrectedCoordinates = this._getViewportCorrectedPlacement();
      this.style.left = `${viewportCorrectedCoordinates.left}rem`;
      this.style.top = `${viewportCorrectedCoordinates.top}rem`;
    }

    _findOffsetParent(e) {
      this._transformParentOffsetX = 0;
      this._transformParentOffsetY = 0;
      const transformParent = e
        .composedPath()
        .filter(
          (parentElement) =>
            parentElement !== this &&
            parentElement.tagName &&
            getComputedStyle(parentElement)['transform'] !== 'none'
        )[0];
      if (transformParent) {
        const transformParentX = transformParent.getBoundingClientRect().left;
        const transformParentY = transformParent.getBoundingClientRect().top;
        this._transformParentOffsetX = transformParentX - transformParent.scrollLeft;
        this._transformParentOffsetY = transformParentY - transformParent.scrollTop;
      }
    }
    _documentMouseDownActions(e) {
      if (
        e.composedPath().indexOf(this) === -1 &&
        e.composedPath().indexOf(this._targetEl) === -1
      ) {
        this.visible = false;
      }
    }
    _documentMouseWheelActions(e) {
      if (e.composedPath().indexOf(this) === -1) {
        this.visible = false;
      }
    }
  };
